import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ReactNode } from 'react';
import { frostedPanelSx } from 'src/common/styles/frostedPanelStyles';

interface EmptyAction {
  label: string;
  onClick: () => void;
}

interface Props {
  icon: ReactNode;
  title: string;
  description: string;
  primaryAction?: EmptyAction;
  secondaryAction?: EmptyAction;
  /** Parent provides the surface (e.g. insights panel Paper). */
  embedded?: boolean;
}

const Empty = ({
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  embedded = false,
}: Props) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        width: '100%',
        py: { xs: 6, sm: 10 },
        px: 2,
        ...(embedded
          ? {}
          : {
              ...frostedPanelSx(0.5),
            }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 48,
          height: 48,
          borderRadius: 2,
          bgcolor: 'grey.100',
          color: 'text.primary',
          mb: 2.5,
        }}
      >
        {icon}
      </Box>

      <Typography variant="h5" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ maxWidth: 420, mb: 3, lineHeight: 1.6 }}
      >
        {description}
      </Typography>

      {(primaryAction || secondaryAction) && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          {primaryAction ? (
            <Button
              variant="contained"
              onClick={primaryAction.onClick}
              sx={{
                bgcolor: 'grey.900',
                color: 'common.white',
                px: 2.5,
                minWidth: { xs: '100%', sm: 140 },
                '&:hover': { bgcolor: 'grey.800' },
              }}
            >
              {primaryAction.label}
            </Button>
          ) : null}
          {secondaryAction ? (
            <Button
              variant="outlined"
              onClick={secondaryAction.onClick}
              sx={{
                borderColor: 'grey.300',
                color: 'text.primary',
                px: 2.5,
                minWidth: { xs: '100%', sm: 140 },
                '&:hover': { borderColor: 'grey.400', bgcolor: 'grey.50' },
              }}
            >
              {secondaryAction.label}
            </Button>
          ) : null}
        </Stack>
      )}
    </Box>
  );
};

export default Empty;
